import { useState } from 'react';
import type { Product } from '../types';
import ConfirmModal from './ConfirmModal';

interface ProductTableProps {
  products: Product[];
  selectedIds: number[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: number, checked: boolean) => void;
  onToggleStatus: (id: number, isOnline: boolean) => void;
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
}

export default function ProductTable({
  products,
  selectedIds,
  onSelectAll,
  onSelectOne,
  onToggleStatus,
  onEdit,
  onDelete,
}: ProductTableProps) {
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    type: 'status' | 'delete';
    id?: number;
    isOnline?: boolean;
  }>({ show: false, type: 'status' });

  const handleConfirm = () => {
    if (confirmModal.type === 'status' && confirmModal.id !== undefined) {
      onToggleStatus(confirmModal.id, confirmModal.isOnline!);
    } else if (confirmModal.type === 'delete' && confirmModal.id !== undefined) {
      onDelete(confirmModal.id);
    }
    setConfirmModal({ show: false, type: 'status' });
  };

  const getConfigDisplay = (product: Product) => {
    const parts = [
      `${product.cpuCores}核`,
      `${product.memory}GB`,
      `${product.systemDisk}GB`,
    ];
    if (product.gpu) {
      parts.push(product.gpu);
    }
    return parts.join(' / ');
  };

  const getPricingDisplay = (product: Product) => {
    const methods = [];
    if (product.pricingMethods.includes('hourly')) methods.push('计时付费');
    if (product.pricingMethods.includes('monthly')) methods.push('包月购买');
    if (product.pricingMethods.includes('package')) methods.push('包段购买');
    return methods.join(' / ');
  };

  return (
    <>
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedIds.length === products.length}
                    onChange={(e) => onSelectAll(e.target.checked)}
                    className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">排序</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">名称</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">分类</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">是否上架</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">配置规格</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">节点信息</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">计价方式</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(product.id)}
                      onChange={(e) => onSelectOne(product.id, e.target.checked)}
                      className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{product.sort}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{product.name}</span>
                      {product.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {product.tags.map((tag) => (
                            <span
                              key={tag}
                              className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                                tag === '新品'
                                  ? 'bg-blue-100 text-blue-700'
                                  : tag === '热卖'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-orange-100 text-orange-700'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {product.categoryName}
                      {product.subCategoryName && (
                        <span className="text-gray-500"> / {product.subCategoryName}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() =>
                        setConfirmModal({
                          show: true,
                          type: 'status',
                          id: product.id,
                          isOnline: !product.isOnline,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        product.isOnline ? 'bg-teal-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          product.isOnline ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{getConfigDisplay(product)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {product.datacenters.slice(0, 2).join('、')}
                      {product.datacenters.length > 2 && (
                        <span className="text-gray-500"> 等{product.datacenters.length}个</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{getPricingDisplay(product)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onEdit(product)}
                        className="text-teal-600 hover:text-teal-700 text-sm font-medium whitespace-nowrap"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() =>
                          setConfirmModal({ show: true, type: 'delete', id: product.id })
                        }
                        className="text-red-600 hover:text-red-700 text-sm font-medium whitespace-nowrap"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-4xl text-gray-400"></i>
            <p className="mt-2 text-sm text-gray-500">暂无数据</p>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.show}
        title={confirmModal.type === 'status' ? '确认操作' : '确认删除'}
        message={
          confirmModal.type === 'status'
            ? `确定要${confirmModal.isOnline ? '上架' : '下架'}该商品吗？`
            : '确定要删除该商品吗？删除后将无法恢复。'
        }
        onConfirm={handleConfirm}
        onCancel={() => setConfirmModal({ show: false, type: 'status' })}
      />
    </>
  );
}
